import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * The few pieces of banquet furniture the building is full of, as low-poly
 * geometry built in code: a banquet chair, a 72 inch round standing and folded,
 * a stack of folded linen, a riser deck, a signage easel.
 *
 * Shared by everything that draws them — the loose objects, the set ballroom,
 * the carts, and the storage stacks in the look layer — so a chair is the same
 * chair wherever it stands. Imports nothing but three: it knows sizes, not
 * rules, and nothing here is ever collided with.
 *
 * Every model is a map of part name to a list of geometries in metres, feet at
 * y = 0, facing +z (a chair's sitter looks toward +z). `merged()` turns one into
 * a single geometry with a group per part, for a mesh with a material array.
 */

const FT = 0.3048;

/** Drops everything but position, normal and uv, so any two can be merged. */
export function clean(geometry) {
  const g = geometry.index ? geometry.toNonIndexed() : geometry;
  for (const name of Object.keys(g.attributes)) if (!['position', 'normal', 'uv'].includes(name)) g.deleteAttribute(name);
  if (!g.attributes.uv) g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(g.attributes.position.count * 2), 2));
  return g;
}

/** A box by its centre and size, with texture coordinates in metres. */
export function block(sx, sy, sz, x = 0, y = 0, z = 0) {
  const g = new THREE.BoxGeometry(Math.max(sx, 0.001), Math.max(sy, 0.001), Math.max(sz, 0.001));
  // BoxGeometry's faces: +x, -x, +y, -y, +z, -z, four corners each.
  const span = [[sz, sy], [sz, sy], [sx, sz], [sx, sz], [sx, sy], [sx, sy]];
  const uv = g.attributes.uv;
  for (let face = 0; face < 6; face += 1) {
    for (let k = 0; k < 4; k += 1) {
      const i = face * 4 + k;
      uv.setXY(i, uv.getX(i) * span[face][0], uv.getY(i) * span[face][1]);
    }
  }
  return g.translate(x, y, z);
}

/** An upright cylinder by its centre. */
export function drum(radiusTop, radiusBottom, height, x = 0, y = 0, z = 0, segments = 16) {
  return new THREE.CylinderGeometry(radiusTop, radiusBottom, height, segments).translate(x, y, z);
}

/** A thin rod from one point to another. */
export function rod(radius, from, to, segments = 6) {
  const a = new THREE.Vector3(...from);
  const b = new THREE.Vector3(...to);
  const length = a.distanceTo(b);
  const g = new THREE.CylinderGeometry(radius, radius, length, segments);
  const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
  g.applyQuaternion(q);
  const mid = a.add(b).multiplyScalar(0.5);
  return g.translate(mid.x, mid.y, mid.z);
}

/** Moves every geometry of a model: turned `yaw` about y, then shifted. */
export function place(model, x = 0, y = 0, z = 0, yaw = 0) {
  const out = {};
  for (const [part, list] of Object.entries(model)) {
    out[part] = list.map((g) => {
      const c = g.clone();
      if (yaw) c.rotateY(yaw);
      return c.translate(x, y, z);
    });
  }
  return out;
}

/** Adds one model's parts to another's. */
export function join(into, model) {
  for (const [part, list] of Object.entries(model)) (into[part] ??= []).push(...list);
  return into;
}

/**
 * One geometry with a group per part, in the order of `parts`; parts missing
 * from the model are empty groups, so the material array always lines up.
 */
export function merged(model, parts) {
  const list = [];
  const groups = [];
  let start = 0;
  for (const [index, part] of parts.entries()) {
    const pieces = (model[part] ?? []).map(clean);
    if (!pieces.length) continue;
    const g = mergeGeometries(pieces);
    for (const p of pieces) p.dispose();
    list.push(g);
    groups.push({ start, count: g.attributes.position.count, materialIndex: index });
    start += g.attributes.position.count;
  }
  const geometry = list.length ? mergeGeometries(list) : new THREE.BufferGeometry();
  for (const g of list) g.dispose();
  geometry.clearGroups();
  for (const group of groups) geometry.addGroup(group.start, group.count, group.materialIndex);
  geometry.computeBoundingSphere();
  return geometry;
}

// --- the furniture --------------------------------------------------------------

/** Seat height of a banquet chair, to the top of its cushion. */
export const CHAIR_SEAT = 0.46;
/** How much higher each chair in a stack sits than the one under it. */
export const CHAIR_NEST = 0.055;

/**
 * A banquet chair: a slim metal frame, a padded seat and a padded back.
 * Parts: `frame`, `cushion`.
 */
export function banquetChair() {
  const w = 0.43;
  const d = 0.43;
  const frame = [];
  const cushion = [];
  const leg = 0.012;
  for (const sx of [-1, 1]) {
    // Front legs straight; back legs carry on up as the back's posts, raked.
    frame.push(rod(leg, [sx * (w / 2 - 0.02), 0, d / 2 - 0.02], [sx * (w / 2 - 0.02), 0.42, d / 2 - 0.02]));
    frame.push(rod(leg, [sx * (w / 2 - 0.02), 0, -d / 2 + 0.01], [sx * (w / 2 - 0.02), 0.42, -d / 2 + 0.03]));
    frame.push(rod(leg, [sx * (w / 2 - 0.02), 0.42, -d / 2 + 0.03], [sx * (w / 2 - 0.03), 0.92, -d / 2 - 0.03]));
    // A stretcher between the legs, low down.
    frame.push(rod(0.008, [sx * (w / 2 - 0.02), 0.14, d / 2 - 0.02], [sx * (w / 2 - 0.02), 0.14, -d / 2 + 0.015]));
  }
  frame.push(block(w - 0.02, 0.02, d - 0.02, 0, 0.41, 0));
  frame.push(block(w - 0.05, 0.025, 0.02, 0, 0.9, -d / 2 - 0.028));
  cushion.push(block(w - 0.01, 0.05, d - 0.01, 0, CHAIR_SEAT - 0.025, 0.005));
  const back = block(w - 0.07, 0.3, 0.04, 0, 0.7, -d / 2 - 0.005);
  back.translate(0, -0.7, d / 2 + 0.005).rotateX(-0.1).translate(0, 0.7, -d / 2 - 0.005);
  cushion.push(back);
  return { frame, cushion };
}

/** A stack of `count` banquet chairs, nested as they stack. */
export function chairStack(count) {
  const out = {};
  const one = banquetChair();
  for (let i = 0; i < count; i += 1) join(out, place(one, 0, i * CHAIR_NEST, -i * 0.012));
  return out;
}

/** A 72 inch round's top, in metres. */
export const ROUND_RADIUS = 3 * FT;
export const ROUND_HEIGHT = 2.5 * FT;

/**
 * A 72 inch round standing on its folding legs. Parts: `top` (the plywood and
 * its edge band), `legs`.
 */
export function roundTable() {
  const r = ROUND_RADIUS;
  const top = [drum(r, r, 0.035, 0, ROUND_HEIGHT - 0.0175, 0, 28)];
  const legs = [];
  // The edge band, a shade darker, just under the lip.
  legs.push(drum(r - 0.005, r - 0.005, 0.015, 0, ROUND_HEIGHT - 0.042, 0, 28));
  for (const sz of [-1, 1]) {
    // Two folding T-legs: a foot bar on the floor, an upright pair, a brace.
    const z = sz * r * 0.42;
    legs.push(block(r * 1.05, 0.03, 0.04, 0, 0.015, z));
    for (const sx of [-1, 1]) legs.push(rod(0.014, [sx * r * 0.3, 0.02, z], [sx * r * 0.3, ROUND_HEIGHT - 0.05, z * 0.85]));
    legs.push(rod(0.01, [0, ROUND_HEIGHT - 0.06, 0], [0, 0.35, z]));
  }
  return { top, legs };
}

/** One round top lying flat, legs folded under it, `thick` metres deep. */
export function roundFlat(thick = 0.06) {
  const r = ROUND_RADIUS;
  return {
    top: [drum(r, r, thick * 0.6, 0, thick * 0.7, 0, 28)],
    legs: [block(r * 1.1, thick * 0.4, 0.04, 0, thick * 0.2, -r * 0.4), block(r * 1.1, thick * 0.4, 0.04, 0, thick * 0.2, r * 0.4)],
  };
}

/**
 * A flip-top round folded in half and stood on its straight edge, across x:
 * a half disc 1.83 m long and 0.91 m high, `thick` deep along z.
 */
export function roundFolded(thick = 0.07) {
  const r = ROUND_RADIUS;
  // The half disc is built lying down, bulging toward +x; stood up, its flat
  // edge runs along x on the floor and it bulges up.
  const half = new THREE.CylinderGeometry(r, r, thick, 24, 1, false, 0, Math.PI).rotateX(Math.PI / 2).rotateZ(Math.PI / 2);
  return { top: [half], legs: [block(r * 2 - 0.1, 0.03, thick + 0.01, 0, 0.015, 0)] };
}

/**
 * Folded linen: a stack of `count` cloths, each folded to `width` by `depth`
 * and 4 cm deep, laid a little askew, the fold a proud roll along one edge.
 * Part: `linen`.
 */
export function linenStack(count = 4, width = 0.36, depth = 0.28) {
  const linen = [];
  for (let i = 0; i < count; i += 1) {
    const y = 0.02 + i * 0.042;
    const skew = (((i * 37) % 7) - 3) * 0.012;
    const shift = ((i % 3) - 1) * 0.006;
    linen.push(block(width - (i % 2) * 0.01, 0.04, depth).rotateY(skew).translate(shift, y, 0));
    linen.push(drum(0.021, 0.021, width - 0.02, 0, 0, 0, 8).rotateZ(Math.PI / 2).translate(0, 0, depth / 2 - 0.018).rotateY(skew).translate(shift, y, 0));
  }
  return { linen };
}

/**
 * A stage riser: a carpeted 4 by 8 foot deck, `height` metres tall, on its
 * folding legs. Parts: `top`, `frame`.
 */
export function riserDeck(width, depth, height) {
  const top = [block(width, 0.03, depth, 0, height - 0.015, 0)];
  const frame = [block(width, 0.07, depth, 0, height - 0.065, 0)];
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) frame.push(block(0.04, height - 0.1, 0.04, sx * (width / 2 - 0.08), (height - 0.1) / 2, sz * (depth / 2 - 0.08)));
    frame.push(rod(0.01, [sx * (width / 2 - 0.08), 0.05, -depth / 2 + 0.08], [sx * (width / 2 - 0.08), height - 0.1, depth / 2 - 0.08]));
  }
  return { top, frame };
}

/**
 * A signage board on an easel, the board facing +z. Parts: `board`, `frame`.
 */
export function signEasel(width, height) {
  const boardH = Math.min(height * 0.62, width * 1.3);
  const boardW = Math.min(width, boardH * 0.78);
  const board = [block(boardW, boardH, 0.02, 0, height - boardH / 2 - 0.05, 0.05)];
  const frame = [
    rod(0.015, [-boardW * 0.35, 0, 0.18], [-boardW * 0.12, height, 0.02]),
    rod(0.015, [boardW * 0.35, 0, 0.18], [boardW * 0.12, height, 0.02]),
    rod(0.015, [0, 0, -0.3], [0, height - 0.08, 0.0]),
    block(boardW + 0.04, 0.03, 0.06, 0, height - boardH - 0.07, 0.07),
  ];
  return { board, frame };
}
